import {
  MachineApiItem,
  MachineGroupApiItem,
  MachineOS,
  MachineRegionId,
  MachineRegionName,
  MachineType,
  MachineTypeDetailedOption,
  MachineTypeGroup,
  MachineTypeInfoApi,
  NormalizedOS,
  regionNames,
  StackApiItem,
  StackGroupApiItem,
  StackOS,
} from '@/types/StackAndMachine';

import { pluralizeWordWithCount } from '../utils/pluralizedWordWithCount';

export function flattenStackGroups(stackGroups: StackGroupApiItem[]): StackApiItem[] {
  return stackGroups.flatMap((group) => group.stacks);
}

export function getStackById(stacks: StackApiItem[], stackId?: string): StackApiItem | undefined {
  return stacks.find((stack) => stack.id === stackId);
}

export function getGroupedMachinesByStack(machineGroups: MachineTypeGroup[], stack?: StackApiItem): MachineTypeGroup[] {
  return machineGroups
    .map((group) => ({
      ...group,
      machineTypes: group.machineTypes.filter((machine) => stack?.machines?.includes(machine.id)),
    }))
    .filter((group) => group.machineTypes.length > 0);
}

export function flattenMachineGroups(machineGroups: MachineTypeGroup[]): MachineType[] {
  return machineGroups.flatMap((group) => group.machineTypes);
}

export function selectableMachines(machineGroups: MachineTypeGroup[], stack?: StackApiItem): MachineType[] {
  return machineGroups
    .flatMap((group) => group.machineTypes)
    .filter(({ id, isDisabled }) => !isDisabled && stack?.machines?.includes(id));
}

export function getMachineById(machines: MachineType[], machineId?: string): MachineType | null {
  return machines.find((machine) => machine.id === machineId) || null;
}

export const toMachineTypeInfoText = (name: string, cpuCount: string, ram: string) => {
  return `${name} ${cpuCount} ${ram}`;
};

function toMachineType(item: MachineApiItem): MachineType {
  let availableInRegions: Partial<Record<MachineRegionName, string[]>> = {};

  (Object.entries(item.availableInRegions) as [MachineRegionId, MachineTypeInfoApi[]][]).forEach(
    ([regionId, regionInfos]) => {
      availableInRegions[regionNames[regionId]] = regionInfos.map((regionInfo) =>
        toMachineTypeInfoText(regionInfo.name, regionInfo.cpuCount, regionInfo.ram),
      );
    },
  );

  return {
    creditPerMinute: item.creditPerMin,
    id: item.id,
    name: item.name,
    os: toMachineOS(item.osId),
    isDisabled: item.isDisabled,
    availableInRegions,
    availableOnStacks: item.availableOnStacks ?? [],
  };
}

const toMachineTypeLabel = (machineType: MachineType) => {
  let label = `${machineType.name}`;

  if (machineType.creditPerMinute) {
    label += ` (${machineType.creditPerMinute} ${pluralizeWordWithCount('credit', machineType.creditPerMinute)}/min)`;
  }

  return label;
};

export const toMachineTypeGroup = (group: MachineGroupApiItem): MachineTypeGroup => {
  return {
    label: group.label,
    machineTypes: group.machines.map(toMachineType),
  } as MachineTypeGroup;
};

export const doesHardwareVaryByRegion = (machineType: MachineType) => {
  const machineTypeInfoTexts = Object.values(machineType.availableInRegions).map((texts) => (texts ?? []).join(', '));

  return machineTypeInfoTexts.some((text) => text !== machineTypeInfoTexts[0]);
};

export function toMachineTypeDetailedOption(
  machineType: MachineType,
  region?: MachineRegionName,
): MachineTypeDetailedOption {
  let subtitle = '';
  if (!region && doesHardwareVaryByRegion(machineType)) {
    subtitle = Object.entries(machineType.availableInRegions)
      .map(([regionName, machineTypeInfoTexts]) => `${regionName}: ${(machineTypeInfoTexts ?? []).join(' • ')}`)
      .join(`\n`);
  } else {
    subtitle = (
      machineType.availableInRegions[region || (Object.keys(machineType.availableInRegions)[0] as MachineRegionName)] ??
      []
    ).join(' • ');
  }

  return {
    isDisabled: machineType.isDisabled,
    title: toMachineTypeLabel(machineType),
    os: machineType.os,
    subtitle,
    value: machineType.id,
  };
}

function toStackOS(os: string): StackOS {
  switch (os.toLowerCase()) {
    case 'linux':
      return 'linux';
    case 'osx':
    case 'macos':
      return 'macos';
    default:
      return 'unknown';
  }
}

function toMachineOS(os?: string): MachineOS {
  switch (os?.toLowerCase()) {
    case 'linux':
      return 'linux';
    case 'osx':
    case 'macos':
      return 'osx';
    default:
      return 'unknown';
  }
}

export function toNormalizedOS(os: string): NormalizedOS {
  return toStackOS(os);
}
